/**
 * AD-021b's scene object: the canopies themselves, minted from the shared silk,
 * stepped on the encounter's clock and returned to the kit when they are down.
 *
 * The *rules* — who goes over the side, which some of them, and whether a jump
 * is worth drawing — are `ambient-jumper-rules.ts`, which imports no three.js
 * and is where the whole design argument is written down. This file is the
 * `scene-crash-marks.ts`-shaped half: a group, an `update` off the encounter, a
 * count for the tests and a `dispose`.
 */

import * as THREE from "three/webgpu";
import {
  beginJump,
  stepParachute,
  type EncounterState,
  type GroundQuery,
  type ParachuteState,
} from "@red-opus/engine";
import {
  AMBIENT_JUMP_DECISION_SECONDS,
  AMBIENT_STEP_HZ,
  AMBIENT_STEP_S,
  AMBIENT_SWAY_RAD,
  MAX_AMBIENT_JUMPERS,
  jumpWorthDrawing,
  jumperDrawn,
  jumperEligible,
} from "./ambient-jumper-rules.ts";
import {
  createCanopyKit,
  CANOPY_COLLAPSE_SECONDS,
  CANOPY_LINGER_SECONDS,
  type CanopyKit,
  type CanopyRig,
} from "./canopy-kit.ts";
import type { EffectsSettings } from "./effects-settings.ts";
import type { EncounterRunner } from "./sim-runner.ts";

export * from "./ambient-jumper-rules.ts";

/**
 * The most steps one update will take for one jumper, so a long stall — a
 * paused tab, a slow region load, a burst of time compression — cannot turn one
 * frame into thousands of integrator steps. A capped jumper simply lags and
 * catches up over the following frames, which is cosmetic.
 */
const MAX_CATCH_UP_STEPS = 32;

interface JumperSlot {
  readonly aircraftId: string;
  readonly rig: CanopyRig;
  state: ParachuteState;
  /** Whole `AMBIENT_STEP_S` chunks of sim time already integrated. */
  steps: number;
  /** Sim time he left the aeroplane — his age is measured against this and nothing else. */
  readonly originTimeS: number;
  /**
   * The step index he arrived on, or `null` while he is still in the air.
   *
   * The *index* rather than a wall time, because the index is what makes the
   * collapse and the despawn frame-independent: he touched down exactly
   * `landedStep * AMBIENT_STEP_S` seconds after `originTimeS` on every replay,
   * whatever the frame boundaries were.
   */
  landedStep: number | null;
}

export interface AmbientJumpers {
  /** Added to the scene once; every canopy draws inside it. */
  readonly group: THREE.Group;
  update(encounter: EncounterState, drawn: boolean): void;
  /** Canopies in the sky or settling — what a test counts. */
  liveCount(): number;
  /** Rigs this module has ever built. The census asserts it stays at the cap. */
  builtCount(): number;
  dispose(): void;
}

class AmbientJumpersImpl implements AmbientJumpers {
  readonly group = new THREE.Group();
  private readonly kit: CanopyKit = createCanopyKit();
  private readonly slots: JumperSlot[] = [];
  /**
   * Aeroplanes already judged, so a man is never reconsidered. Bounded by the
   * roster — every entry is an aircraft id that reached the trigger once.
   */
  private readonly decided = new Set<string>();
  private lastTimeS: number | null = null;
  private disposed = false;

  /**
   * The region's ground, through the same swappable ref the sim, the shadows
   * and the crash marks read — a ref rather than a value because
   * `fly-region.ts` fills it after the scene is built.
   *
   * **Every rig this module will ever use is built here, at boot, and hidden.**
   * Not when a man jumps: `CanopyRig.setVisible` records what the lazy version
   * cost — the buffers went up on the jump frame, three.js threw out of the
   * upload, and the render loop died with the pilot correctly under a canopy
   * nobody could see. It is also the convention `scene-particles.ts` and
   * `scene-shadows.ts` both state outright ("always built, whatever the toggle
   * says"), for the milder version of the same reason.
   */
  constructor(private readonly groundRef: { readonly current: GroundQuery }) {
    // Minted all at once and only then released, because `mint` re-uses the
    // free list — releasing inside the loop would hand the same rig back every
    // iteration and build a pool of one.
    const pool = Array.from({ length: MAX_AMBIENT_JUMPERS }, () => this.kit.mint());
    for (const rig of pool) {
      this.group.add(rig.group);
      rig.release();
    }
  }

  update(encounter: EncounterState, drawn: boolean): void {
    if (this.disposed) return;
    this.group.visible = drawn;
    if (!drawn) {
      // The crash marks' bargain: with the toggle off a sortie's pixels are what
      // they were before this feature existed. `decided` is kept across the
      // flip on purpose, so switching it back on does not empty every burning
      // cockpit at once.
      this.clear();
      return;
    }
    // Time compression and a paused sim both come through here as a `timeS`
    // that jumped or did not move; neither needs a special case, because every
    // jumper's age is read from `timeS` rather than accumulated.
    this.lastTimeS = encounter.timeS;
    const ground = this.groundRef.current;
    this.spawn(encounter, ground);
    this.advance(encounter, ground);
  }

  private spawn(encounter: EncounterState, ground: GroundQuery): void {
    for (const aircraft of encounter.aircraft) {
      if (this.decided.has(aircraft.id)) continue;
      if (!jumperEligible(aircraft)) continue;
      // Judged once and only once, whether or not he goes — which is what stops
      // a man appearing thirty seconds late when a slot frees. He is only going
      // to get lower from here, so a refusal below never wants revisiting.
      this.decided.add(aircraft.id);
      // The hash before the probe, deliberately: it is two integer operations
      // and it rejects four aeroplanes in five, so the 255-step probe below is
      // only ever run for a man who is actually going to jump.
      if (!jumperDrawn(aircraft.id, encounter.seed)) continue;
      if (this.slots.length >= MAX_AMBIENT_JUMPERS) continue;
      const { x, y, z } = aircraft.sim.position;
      if (!jumpWorthDrawing(aircraft.sim, ground.groundHeightAt(x, z))) continue;
      // From the pool built at boot; `mint` never allocates here because the
      // cap and the pool are the same number.
      const rig = this.kit.mint();
      rig.group.position.set(x, y, z);
      this.slots.push({
        aircraftId: aircraft.id,
        rig,
        // AD-021's own opening, unmodified: he leaves with the aeroplane's
        // position and its velocity, which is what throws a man out of a spiral
        // further than out of a glide.
        state: beginJump({ pilotId: aircraft.id, sim: aircraft.sim }),
        steps: 0,
        // Exact and frame-independent: the trigger crossed
        // `AMBIENT_JUMP_DECISION_SECONDS` this many seconds ago in *sim* time.
        originTimeS: encounter.timeS - (aircraft.fallingSeconds - AMBIENT_JUMP_DECISION_SECONDS),
        landedStep: null,
      });
    }
  }

  private advance(encounter: EncounterState, ground: GroundQuery): void {
    for (let i = this.slots.length - 1; i >= 0; i -= 1) {
      const slot = this.slots[i]!;
      const ageS = Math.max(0, encounter.timeS - slot.originTimeS);
      // `* AMBIENT_STEP_HZ`, never `/ AMBIENT_STEP_S`. The two are the same
      // number in arithmetic and not in floating point: `1/30` is not
      // representable, so `12.5 / (1/30)` lands either side of 375 depending on
      // how `12.5` was arrived at, and the floor of it is therefore 374 on one
      // sampling and 375 on another. That is a one-step, 18 cm divergence
      // between two replays of the same tape — which is exactly what this
      // module promises cannot happen, and what `ambient-jumpers.test.ts`'s
      // frame-rate test caught. Multiplying by the integer rate is exact.
      const wanted = Math.floor(ageS * AMBIENT_STEP_HZ);
      let taken = 0;
      while (slot.steps < wanted && taken < MAX_CATCH_UP_STEPS) {
        const { x, z } = slot.state.position;
        slot.state = stepParachute(slot.state, {
          dt: AMBIENT_STEP_S,
          // The **drawn** surface, through the composed query the undercarriage
          // stops on and the shadows drape to — never the analytic relief field.
          // A canopy that settled on a different surface from the one the player
          // is looking at is the fault class `lessons.md` records four times.
          groundHeightM: ground.groundHeightAt(x, z),
        });
        slot.steps += 1;
        taken += 1;
        if (slot.landedStep === null && slot.state.status !== "falling" && slot.state.status !== "open") {
          slot.landedStep = slot.steps;
        }
      }

      const state = slot.state;
      if (state.status === "dead") {
        // He arrived faster than silk survives. There is nothing to draw and
        // nothing to celebrate; the rig goes back to the kit at once.
        this.retire(i);
        continue;
      }
      slot.rig.group.position.set(state.position.x, state.position.y, state.position.z);
      slot.rig.setOpen(state.status !== "falling");
      if (slot.landedStep === null) {
        slot.rig.setCollapse(0);
        slot.rig.setSway(ageS, AMBIENT_SWAY_RAD);
        continue;
      }
      // Down. The clock from here is the sim's, keyed off the step he arrived
      // on, so a replay collapses and despawns the canopy at the same moment of
      // the tape rather than at the same number of frames.
      const settledS = ageS - slot.landedStep / AMBIENT_STEP_HZ;
      if (settledS >= CANOPY_COLLAPSE_SECONDS + CANOPY_LINGER_SECONDS) {
        this.retire(i);
        continue;
      }
      slot.rig.setCollapse(settledS / CANOPY_COLLAPSE_SECONDS);
      // A canopy lying in a field does not rock.
      slot.rig.setSway(ageS, 0);
    }
  }

  private retire(index: number): void {
    const slot = this.slots[index];
    if (!slot) return;
    slot.rig.release();
    this.slots.splice(index, 1);
  }

  private clear(): void {
    for (let i = this.slots.length - 1; i >= 0; i -= 1) this.retire(i);
  }

  liveCount(): number {
    return this.slots.length;
  }

  builtCount(): number {
    return this.kit.builtCount();
  }

  /** The last sim time this module saw, for the tests that assert it never ran. */
  seenTimeS(): number | null {
    return this.lastTimeS;
  }

  dispose(): void {
    if (this.disposed) return;
    this.clear();
    this.disposed = true;
    this.group.removeFromParent();
    this.kit.dispose();
  }
}

/**
 * Builds the canopies and attaches them to the **scene**, not to any group with
 * a teardown of its own — `lessons.md`'s cockpit-dash entry: *"a group that gets
 * `clear()`ed is a group nothing else may live in"*, and `scene-boot.ts`'s
 * `loadAircraft` empties the player's model group on every ENHANCED MODELS flip.
 */
export function createAmbientJumpers(
  scene: THREE.Scene,
  groundRef: { readonly current: GroundQuery },
): AmbientJumpers {
  const jumpers = new AmbientJumpersImpl(groundRef);
  scene.add(jumpers.group);
  return jumpers;
}

export interface AmbientJumperHost {
  readonly runner: EncounterRunner;
  readonly effects: EffectsSettings;
  readonly jumpers: AmbientJumpers;
}

/** Whether ambience jumpers are drawn this frame — **ENHANCED EFFECTS, and nothing else**. */
export function ambientJumpersDrawn(host: AmbientJumperHost): boolean {
  return host.effects.enhancedEffects;
}

export function updateAmbientJumpers(host: AmbientJumperHost): void {
  host.jumpers.update(host.runner.encounter(), ambientJumpersDrawn(host));
}
